import * as Sentry from "@sentry/browser"
import { Integration } from "@sentry/types/types/integration"
const appConfiguration = require("./config/sentry.json")
const sentryDns = appConfiguration.integrations?.sentry?.dsn
const sentryEnabled = appConfiguration.settings.enableSentryIntegration
const sentryEnvironment = appConfiguration.settings?.sentryOptionEnv
const sentryReplaySessionEnabled =
  appConfiguration.settings.sentrySessionReplayEnable
const sentryTracesSampleEnable =
  appConfiguration.settings.sentryTracesSampleEnable
const sentryTracesSampleRate = appConfiguration.settings.sentryTracesSampleRate
const sentryTracePropagationTargets =
  appConfiguration.settings.sentryTracePropagationTargets
const sentryCaptureConsoleEnable =
  appConfiguration.settings.sentryCaptureConsoleEnable
const sentryHttpClientEnable = appConfiguration.settings.sentryHttpClientEnable

const integrations: Integration[] = [
  Sentry.replayIntegration(),
  Sentry.browserTracingIntegration({
    enableInp: true,
  }),
]

if (sentryCaptureConsoleEnable) {
  integrations.push(
    Sentry.captureConsoleIntegration({
      levels: ["log", "info", "warn", "error"],
    })
  )
}

if (sentryHttpClientEnable) {
  integrations.push(Sentry.httpClientIntegration())
}

const sentrySendErrorsToGoogleAnalytic =
  appConfiguration.settings.sentrySendErrorsToGoogleAnalytic

declare global {
  interface Window {
    dataLayer: any[]
    dataLayer2: any[]
  }
}

// default options
const options: Sentry.BrowserOptions = {
  dsn: sentryDns,
  environment: sentryEnvironment,
  release: `${process.env.GATSBY_APP_VERSION}`,
  integrations,
  tracesSampleRate: 0,
  tracePropagationTargets: [],
  beforeSend(event, hint) {
    const userAgent = navigator.userAgent.toLowerCase()
    const blockedUserAgents = ["bytespider", "bytedance"]
    const isBlocked = blockedUserAgents.some(blockUserAgent =>
      userAgent.includes(blockUserAgent)
    )
    if (isBlocked) {
      return null
    }

    try {
      const value = event?.exception?.values?.[0]
      const type = value?.type || "Error"
      const message =
        event?.message ||
        value?.value ||
        (hint && hint.originalException && String(hint.originalException)) ||
        "Unknown error"

      const analyticEvent = {
        event: "exception",
        description: `${type}: ${message}`.slice(0, 150),
        fatal: event.level === "fatal" || false,
        sentry_event_id: event.event_id,
        sentry_level: event.level || "error",
        sentry_release: event.release,
        sentry_env: event.environment,
      }

      if (window && sentrySendErrorsToGoogleAnalytic) {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push(analyticEvent)

        window.dataLayer2 = window.dataLayer2 || []
        window.dataLayer2.push(analyticEvent)
      }
    } catch (e) {
      // do nothing
    }

    return event
  },
}

// trace sample options
if (sentryTracesSampleEnable) {
  options.tracesSampleRate = sentryTracesSampleRate / 100
  options.tracePropagationTargets = [
    "localhost",
    ...sentryTracePropagationTargets,
  ]
}

// session replay options
if (sentryReplaySessionEnabled) {
  options.replaysSessionSampleRate =
    appConfiguration.settings.sentrySessionReplaySampleRate / 100
  options.replaysOnErrorSampleRate =
    appConfiguration.settings.sentrySessionReplayOnErrorSampleRate / 100
}

if (sentryEnabled) {
  Sentry.init(options)
}
